import KmxArrowRightFilled from '@kmx/mui-icons/KmxArrowRightFilled';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import GlobalStyles from '@mui/material/GlobalStyles';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import { getIconForType } from './categoryIcons';
import cookieHelper from './cookieHelper';
import RecommendedCategoriesHeader from './RecommendedCategoriesHeader';
import RecommendedCategoriesSkeleton from './RecommendedCategoriesSkeleton';

const defaultBaseUrl = `${window.location.origin}/cars/api/search/recommendedcategories`;

const MAX_CARDS_ON_DESKTOP = 3;

export interface CategoryItem {
  categoryKey: string;
  categoryName: string;
  iconType: string;
  searchToken: string;
  totalCount: number;
  url: string;
}

export interface RecommendedCategoriesData {
  displayTitle: string;
  displaySubtitle: string | null;
  categories: CategoryItem[];
  displayOrder: number;
  componentType: string;
  componentKey: string;
}

export interface RecommendedCategoriesProps {
  heroThumbnailImageUrl: string;
  stockNumber: string;
  pageType: string;
  customBaseUrl?: string;
  customData?: RecommendedCategoriesData;
  isReturnVisit?: boolean;
  onRecommendedCategoryClick?: (
    categoryData: RecommendedCategoriesData,
    selectedCategory: CategoryItem,
  ) => void;
  containerId?: string;
}

interface CategoryCardProps {
  category: CategoryItem;
  index: number;
  onRecommendedCategoryClick?: (selectedCategory: CategoryItem) => void;
}

const CategoryCard = ({
  category,
  index,
  onRecommendedCategoryClick,
}: CategoryCardProps) => {
  const subtitle = `${category.totalCount.toLocaleString()} match${category.totalCount !== 1 ? 'es' : ''}`;
  const iconImage = getIconForType(category.iconType);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 1.5,
        display: { lg: index >= MAX_CARDS_ON_DESKTOP ? 'none' : 'block' },
        minWidth: 0,
        flexGrow: { lg: 1 },
        flexBasis: { lg: 0 },
      }}
    >
      <CardActionArea
        href={category.url}
        onClick={
          onRecommendedCategoryClick
            ? () => onRecommendedCategoryClick(category)
            : undefined
        }
        sx={{
          height: '100%',
          '&:hover .MuiCardActionArea-focusHighlight': {
            backgroundColor: `#b3b8c2`,
          },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center', p: 1, pr: 2 }}
        >
          <CardMedia
            image={iconImage}
            title={category.categoryName}
            sx={{
              minWidth: { xs: '3rem', lg: '5rem' },
              height: { xs: '3rem', lg: '5rem' },
              borderRadius: 1,
              flexShrink: 0,
            }}
          />
          <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              component="h3"
              variant="h6"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {category.categoryName}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          </Stack>
          <KmxArrowRightFilled color="primary" sx={{ flexShrink: 0 }} />
        </Stack>
      </CardActionArea>
    </Card>
  );
};

const RecommendedCategories = ({
  heroThumbnailImageUrl,
  stockNumber,
  pageType,
  customBaseUrl,
  isReturnVisit,
  onRecommendedCategoryClick,
  customData,
  containerId,
}: RecommendedCategoriesProps) => {
  const [data, setData] = useState<RecommendedCategoriesData | null>(
    customData || null,
  );
  const [loading, setLoading] = useState<boolean>(!customData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (customData) {
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (typeof window !== 'undefined') {
          const storeId = cookieHelper.GetCookieItemValue('StoreId') || '';
          const visitorId = cookieHelper.GetCookieItemValue('VisitorID') || '';
          if (!storeId || !visitorId) {
            if (isMounted) {
              setError('StoreId or VisitorId is missing');
            }
            return;
          }

          const url = new URL(customBaseUrl || defaultBaseUrl);

          const params = new URLSearchParams({
            storeId,
            visitorId,
            stockNumber,
            pageType,
            isReturnVisit: isReturnVisit?.toString() ?? 'false',
          });

          url.search = params.toString();

          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`API request failed: ${response.status}`);
          }
          const responseData: RecommendedCategoriesData = await response.json();

          if (isMounted) {
            setData(responseData);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [stockNumber, pageType, customBaseUrl, customData, isReturnVisit]);

  if (loading) {
    return <RecommendedCategoriesSkeleton />;
  }

  if (!data || data.categories?.length === 0 || error) {
    if (containerId) {
      return (
        <GlobalStyles
          styles={{
            [`#${containerId}`]: {
              display: 'none !important',
            },
          }}
        />
      );
    } else {
      return null;
    }
  }

  const { categories = [], displayTitle, displaySubtitle } = data;

  return (
    <Stack spacing={{ xs: 1.5, lg: 5 }} direction={{ lg: 'row' }} useFlexGap>
      <RecommendedCategoriesHeader
        heroThumbnailImageUrl={heroThumbnailImageUrl}
        headingText={displayTitle}
        headingOverlineText={displaySubtitle}
      />
      <Stack
        spacing={{ xs: 1, lg: 1.5 }}
        direction={{ lg: 'row' }}
        sx={{
          alignItems: { lg: 'center' },
          textOverflow: 'ellipsis',
          flexGrow: { lg: 1 },
        }}
        useFlexGap
      >
        {categories
          .filter((category) => category.totalCount > 0)
          .map((category, index) => (
            <CategoryCard
              index={index}
              key={category.categoryKey}
              category={category}
              onRecommendedCategoryClick={(selectedCategory) =>
                onRecommendedCategoryClick?.(data, selectedCategory)
              }
            />
          ))}
      </Stack>
    </Stack>
  );
};

export default RecommendedCategories;
